import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Gift, Sparkles, Heart, Globe } from "lucide-react";

// Public landing for an inbound gift_request (someone's "wishlist link").
// No login required. Payments are MOCKED.
const PublicGiftRequestPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wish, setWish] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(8);
  const [customAmount, setCustomAmount] = useState("");
  const [contribName, setContribName] = useState("");
  const [contribEmail, setContribEmail] = useState("");
  const [contribMessage, setContribMessage] = useState("");
  const [contributing, setContributing] = useState(false);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([
        axios.get(`${API}/gift-requests/r/${slug}`),
        axios.get(`${API}/gift-requests/r/${slug}/contributions`),
      ]);
      setWish(w.data);
      setContributions(c.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Wishlist not found");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [slug]);

  // Stripe redirect-back handler. Polls /api/payments/status until paid+fulfilled,
  // then shows the thank-you state and refreshes contribution totals.
  useEffect(() => {
    const sid = searchParams.get('payment_session_id');
    const cancelled = searchParams.get('payment_cancelled');
    if (cancelled) {
      toast.info("Contribution cancelled — no charge was made.");
      searchParams.delete('payment_cancelled');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sid) return;
    let stop = false, attempts = 0;
    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sid}`);
        const d = res.data;
        if (d.status === 'paid' && d.fulfilled) {
          const credits = Math.round((d.amount_usd || 0) * 25);
          setSuccess({
            credits_added: credits,
            thank_you: `Your ${credits} credits just landed in @${wish?.beneficiary_username}'s account.`,
          });
          toast.success(`+${credits} credits delivered!`);
          await load();
          searchParams.delete('payment_session_id');
          setSearchParams(searchParams, { replace: true });
          setContributing(false);
          return;
        }
        if (['expired', 'failed'].includes(d.status)) {
          toast.error(`Payment ${d.status}.`);
          searchParams.delete('payment_session_id');
          setSearchParams(searchParams, { replace: true });
          setContributing(false);
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
      } catch (err) { /* keep retrying */ }
    };
    toast.info("Confirming your contribution with Stripe…");
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('payment_session_id'), wish?.beneficiary_username]);

  const isCustom = selectedTier === "custom";
  const amount = isCustom ? parseFloat(customAmount) || 0 : Number(selectedTier);
  const credits = wish ? Math.round(amount * (wish.rate_credits_per_usd || 25)) : 0;

  const handleContribute = async (e) => {
    e?.preventDefault?.();
    if (amount <= 0) return toast.error("Pick an amount first");
    if (amount > 1000) return toast.error("Max $1000 per contribution");
    setContributing(true);
    try {
      const res = await axios.post(`${API}/payments/wishlist/contribute/checkout`, {
        slug, amount_usd: amount,
        contributor_name: contribName || null,
        contributor_email: contribEmail || null,
        contributor_message: contribMessage || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start contribution checkout");
      setContributing(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading…</div>;
  if (error) return (
    <div className="min-h-screen bg-black flex items-center justify-center text-center p-6">
      <div>
        <p className="text-rose-300" data-testid="public-wishlist-error">{error}</p>
        <Link to="/"><Button variant="outline" className="mt-4 border-gray-700 text-gray-200">Go to GlobalEyes.TV</Button></Link>
      </div>
    </div>
  );

  if (!wish) return null;

  const tiers = wish.amount_tiers_usd || [4, 8, 20, 40];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950/30 via-gray-950 to-black py-8 px-4" data-testid="public-wishlist-page">
      <div className="container mx-auto max-w-2xl">
        {/* Brand bar */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 opacity-80 hover:opacity-100 transition">
          <Globe className="h-5 w-5 text-emerald-500" />
          <span className="text-lg font-semibold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">GlobalEyes.TV</span>
        </Link>

        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="text-center">
            <Avatar className="h-20 w-20 mx-auto mb-3 border-2 border-emerald-500/40">
              <AvatarImage src={wish.beneficiary_profile_image} />
              <AvatarFallback className="bg-emerald-700 text-white text-xl">{(wish.beneficiary_username || "?")[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <CardTitle className="text-white text-2xl" data-testid="wishlist-headline">{wish.headline}</CardTitle>
            <p className="text-emerald-300 text-sm">@{wish.beneficiary_username}</p>
          </CardHeader>
          <CardContent>
            {wish.message && (
              <p className="text-gray-300 text-center mb-4 italic" data-testid="wishlist-message">"{wish.message}"</p>
            )}
            <div className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-center text-sm text-gray-400">
              <Sparkles className="h-4 w-4 inline text-emerald-400 mr-1" />
              Every $1 = <span className="text-emerald-300 font-semibold">{wish.rate_credits_per_usd || 25} credits</span> for @{wish.beneficiary_username}
            </div>
          </CardContent>
        </Card>

        {success ? (
          <Card className="bg-emerald-950/30 border-emerald-700 text-center" data-testid="contrib-success-card">
            <CardContent className="py-8">
              <Heart className="h-12 w-12 text-pink-400 mx-auto mb-3" />
              <p className="text-emerald-200 text-lg font-semibold">Thank you!</p>
              <p className="text-gray-300 mt-2">{success.thank_you}</p>
              <p className="text-emerald-300 text-sm mt-1">+{success.credits_added} credits delivered</p>
              <div className="grid grid-cols-2 gap-2 mt-6 max-w-xs mx-auto">
                <Button onClick={() => { setSuccess(null); setSelectedTier(8); setCustomAmount(""); setContribName(""); setContribEmail(""); setContribMessage(""); }}
                  className="bg-emerald-700 hover:bg-emerald-600">Give again</Button>
                <Link to={`/${wish.beneficiary_username}`}><Button variant="outline" className="border-gray-700 text-gray-200 w-full">See their videos</Button></Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Gift className="h-5 w-5 text-pink-400" /> Pick an amount
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleContribute} className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tier-grid">
                  {tiers.map((t) => (
                    <button type="button" key={t} onClick={() => setSelectedTier(t)} data-testid={`tier-${t}`}
                      className={`py-3 rounded-lg border font-semibold transition ${selectedTier === t ? "bg-emerald-600 border-emerald-500 text-white" : "bg-gray-950 border-gray-800 text-gray-300 hover:border-gray-700"}`}>
                      ${t}
                    </button>
                  ))}
                  <button type="button" onClick={() => setSelectedTier("custom")} data-testid="tier-custom"
                    className={`py-3 rounded-lg border font-semibold transition ${selectedTier === "custom" ? "bg-emerald-600 border-emerald-500 text-white" : "bg-gray-950 border-gray-800 text-gray-300 hover:border-gray-700"}`}>
                    Custom
                  </button>
                </div>

                {isCustom && (
                  <div>
                    <Label htmlFor="custom-amt" className="text-gray-300">Custom amount (USD)</Label>
                    <Input id="custom-amt" data-testid="custom-amount-input" type="number" min="1" max="1000" step="0.01"
                      value={customAmount} onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="e.g. 12" className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>
                )}

                {amount > 0 && (
                  <p className="text-center text-gray-300 text-sm" data-testid="contribution-preview">
                    You give <span className="text-pink-300 font-semibold">${amount.toFixed(2)}</span> →
                    @{wish.beneficiary_username} gets <span className="text-emerald-300 font-semibold">{credits} credits</span>
                  </p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cn" className="text-gray-300">Your name (optional)</Label>
                    <Input id="cn" data-testid="contributor-name-input" value={contribName} onChange={(e) => setContribName(e.target.value)} className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="ce" className="text-gray-300">Email (optional, for receipt)</Label>
                    <Input id="ce" data-testid="contributor-email-input" type="email" value={contribEmail} onChange={(e) => setContribEmail(e.target.value)} className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cm" className="text-gray-300">Note for @{wish.beneficiary_username} (optional)</Label>
                  <Textarea id="cm" data-testid="contributor-message-input" value={contribMessage} onChange={(e) => setContribMessage(e.target.value)} maxLength={280} rows={2}
                    placeholder="Make us proud!" className="bg-gray-950 border-gray-800 text-white mt-1" />
                </div>

                <Button type="submit" disabled={contributing || amount <= 0} data-testid="contribute-submit"
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white">
                  {contributing ? "Sending..." : `Give $${amount > 0 ? amount.toFixed(2) : "—"}`}
                </Button>
                <p className="text-gray-500 text-xs text-center">Secure checkout via Stripe.</p>
              </form>
            </CardContent>
          </Card>
        )}

        {contributions.length > 0 && (
          <Card className="bg-gray-900 border-gray-800 mt-6">
            <CardHeader>
              <CardTitle className="text-white text-base">Recent supporters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="contributors-list">
              {contributions.map((c, i) => (
                <div key={c.id || `${c.contributor_name || 'anon'}-${c.created_at || i}`} className="flex items-center justify-between bg-gray-950 border border-gray-800 rounded-lg p-2 px-3">
                  <div className="text-sm">
                    <span className="text-emerald-300 font-medium">{c.contributor_name || "Anonymous"}</span>
                    {c.contributor_message && <span className="text-gray-400 italic"> · "{c.contributor_message}"</span>}
                  </div>
                  <span className="text-pink-300 text-sm">+{c.amount_credits} cr</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicGiftRequestPage;
